import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import filter from 'ramda/src/filter';
import values from 'ramda/src/values';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import CommunityActions from '~/actions/community';
import VendorActions from '~/actions/vendor';
import { api } from '~/sagas';
import { ProfileBlogDropOptions, ProfileBlogTabs } from '~/services/config';
import { getCompanyName } from '~/services/util';
import Recent from './Recent';
import Categories, { CategoryFeed } from './Categories';
import Departments, { DepartmentFeed } from './Departments';
import Detail from './Detail';

const Blog = ({ data = {}, isAdmin = false }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const blogHeader = useRef();

    const token = useSelector((state) => state.app.token);
    const departments = useSelector((state) => state.company.blogCategories);
    const blog = data.vrs?.blog;
    const categories = values(blog?.data?.blog_categories) || blog?.data?.categories;

    const [detailPost, setDetailPost] = useState(null);
    const [active, setActive] = useState('recent');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [allcategories, setAllCategories] = useState(false);

    const getBusinessBlog = (payload) => {
        if (token) dispatch(CompanyActions.getbusinessblogRequest(payload));
    };

    const handleTab = (e) => {
        setActive(e.name);
        setSelectedCategory(null);
        setSelectedDepartment(null);
        setDetailPost(null);
    };

    const handleDropdown = (e) => {
        if (e === 'add post') {
            const payload = {
                type: 'Edit Blog Post',
                data: { before: { post: null, businessId: data.id, blog } },
            };
            dispatch(AppActions.modalRequest(payload));
        } else if (e === 'reports') {
        } else {
            navigate(`${location.pathname}-admin?active=${e}`);
        }
    };

    const handleSelectCategory = (category) => {
        setActive('categories');
        setSelectedCategory(category);
        setSelectedDepartment(null);
        getBusinessBlog({ businessId: data.id, payload: { blog_categories: category.name } });
    };

    const handleSelectDepartment = (department) => {
        setActive('departments');
        setSelectedCategory(null);
        setSelectedDepartment(department);
        getBusinessBlog({ businessId: data.id, payload: { category_id: department.id } });
    };

    const handlePagination = (payload) => getBusinessBlog({ businessId: data.id, payload });

    const handleSelectMenu = (item) => (e) => {
        switch (e) {
            case 'delete':
                handleDeleteConfirm(item);
                break;

            case 'edit':
                handleAddCategory(item);
                break;

            default:
                break;
        }
    };

    const handleAddCategory = (item) => {
        const payload = {
            type: 'Add Category',
            data: {
                before: { type: 1, data: item, blog, companyId: data.id, userId: data?.user_id },
                after: { type: 'GETBUSINESS_REQUEST', payload: { id: data.id } },
            },
        };
        dispatch(AppActions.modalRequest(payload));
    };

    const handleDeleteItem = (item) => {
        let newCategories = filter((x) => x.name !== item.name, categories);
        newCategories = newCategories.map((x) => x.name);
        const payload = {
            data: { blog: { ...blog, data: { categories: newCategories } } },
            after: { type: 'GETBUSINESS_REQUEST', payload: { id: data.id } },
        };
        dispatch(VendorActions.saveblogRequest(payload));
    };

    const handleDeleteConfirm = (item) => {
        const payload = {
            type: 'Confirm',
            data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
            callBack: {
                onYes: () => handleDeleteItem(item),
            },
        };
        dispatch(AppActions.modalRequest(payload));
    };

    const handleSetFlag = (payload) => {
        dispatch(CommunityActions.postcontentflagRequest(payload));
    };

    const handlePosts = (e) => {
        navigate(e.data?.permalink);
    };

    const handleFetchDetailPost = async () => {
        if (params?.entity && params?.post) {
            const res = await api.getBlogSlug(params.entity, params.post);
            setDetailPost(res?.data?.posts?.data[0]);
        }
    };

    useEffect(() => {
        blogHeader?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [detailPost]);

    useEffect(() => {
        handleFetchDetailPost();
    }, [location.key]);

    return (
        <ErrorBoundary className="company-profile-blog d-flex ssm-column" ref={blogHeader}>
            <div className="blogs d-flex-12 d-flex-md-3">
                <div className="card">
                    <div className="d-flex ssm-column jc-between mb-30">
                        <span className="dsl-b22 bold">{getCompanyName(data)} Blog</span>
                        {isAdmin && <EditDropdown options={ProfileBlogDropOptions} onChange={handleDropdown} />}
                    </div>
                    <ResponsiveTabs active={active} data={ProfileBlogTabs} getId={(e) => e.name} getValue={(e) => e.label} onTab={handleTab} />
                </div>
                <Tab eventKey="recent" active={active}>
                    {detailPost ? (
                        <Detail data={detailPost} blog={blog} businessId={data.id} onRefresh={handleFetchDetailPost} onSelect={handlePosts} onSetFlag={handleSetFlag} />
                    ) : (
                        <Recent data={blog?.data?.posts} onClick={handlePosts} onSetFlag={handleSetFlag} />
                    )}
                </Tab>
                <Tab eventKey="categories" active={active}>
                    {selectedCategory ? (
                        <CategoryFeed category={selectedCategory} posts={blog?.data?.posts} onChangePage={handlePagination} />
                    ) : (
                        <Categories categories={categories} onDetail={handleSelectCategory} onSelect={handleSelectMenu} />
                    )}
                </Tab>
                <Tab eventKey="departments" active={active}>
                    {selectedDepartment ? (
                        <DepartmentFeed department={selectedDepartment} posts={blog?.data?.posts} onChangePage={handlePagination} />
                    ) : (
                        <Departments departments={departments} onSelect={handleSelectDepartment} />
                    )}
                </Tab>
            </div>
            <div className="d-flex-4 d-flex-sm-12 ps-2 ssp-l-0">
                <div className="card">
                    <p className="dsl-b18 bold mt-0 ssm-t-3">Categories</p>
                    {categories.map((category, index) => {
                        const { name } = category;
                        if (allcategories || index < 5) {
                            return (
                                <Button className="dsl-b14 jc-start px-0 text-start" key={name} type="link" onClick={() => handleSelectCategory(category)}>
                                    <span className="truncate-one dsl-p14 text-secondary">{name}</span>
                                </Button>
                            );
                        }
                        return null;
                    })}
                    {categories.length > 5 && (
                        <div className="d-h-end">
                            <Button type="link" className="px-0" onClick={() => setAllCategories(!allcategories)}>
                                <span className="dsl-p14">{allcategories ? 'See less' : 'See All'}</span>
                            </Button>
                        </div>
                    )}
                </div>
                <div className="card">
                    <p className="dsl-b18 bold mt-0 ssm-t-3">Departments</p>
                    {departments.map((department, index) => {
                        const { id, name } = department;
                        return (
                            <Button className="dsl-b14 jc-start px-0 text-start" key={id} type="link" onClick={() => handleSelectDepartment(department)}>
                                <span className="truncate-one dsl-p14 text-secondary">{name}</span>
                            </Button>
                        );
                    })}
                </div>
            </div>
        </ErrorBoundary>
    );
};

Blog.propTypes = {
    data: PropTypes.any,
    isAdmin: PropTypes.bool,
};

export default Blog;
